// Pack-for-flight — pre-cache tiles + API data for an active route so
// the EFB keeps working offline (lost cell service, behind a ridge,
// in a canyon, in smoke). See docs/pack-for-flight-spec.md for the
// full spec; this file is the MVP.
//
// Strategy:
//   1. Compute the route corridor: densified great-circle line +
//      a 50 nm buffer on each side.
//   2. Enumerate all tile (z, x, y) triples for our base imagery,
//      DEM, and any active chart overlay across z = ZOOM_MIN..ZOOM_MAX
//      that intersect the corridor bbox.
//   3. Enumerate per-airport API URLs for every airport in the
//      corridor bbox (airport detail, plates list).
//   4. Enumerate bbox-scoped API URLs (airports listing, navaids,
//      fixes, fires, viirs, tfrs, sua, sigmet, airmet, plus the
//      common weather endpoints scoped to corridor airports).
//   5. Fetch them in a bounded-concurrency loop. The service worker
//      transparently stashes each response in the right cache.
//   6. Update a zustand store with progress so the UI can render a
//      pill / toast.
//
// ── Caching architecture (ME-19) ──────────────────────────────────────────
// pack.ts does NOT maintain its own in-memory tile cache.  All fetched bytes
// land in the SW's Cache Storage buckets (fyrefly-tiles-v*, fyrefly-api-v*).
// The SW owns eviction via true LRU backed by an IDB sidecar (HI-6, shipped
// 2026-05-22): entries are sorted by lastAccess and evicted oldest-first
// when the byte budget is exceeded.  Frequently accessed tiles always
// survive; touched-once-and-forgotten entries are evicted first.
//
// An age-based or FIFO in-memory prune in pack.ts would be redundant and
// counterproductive — the SW LRU already does the right thing.  If you ever
// need to add an in-memory layer (e.g. for sub-SW hot-path perf), mirror the
// Map-insertion-order LRU pattern from src/lib/terrainSampler.ts and track
// lastAccess per entry (not insertion age).
//
// Persisted manifest is stored in localStorage (simple, plenty of
// room for one MVP pack). When we add the saved-packs UI, we'll
// move this to IndexedDB.

import { create } from 'zustand';
import { greatCircleNm, densifyRouteForGreatCircle } from './geo';
import { bboxLayerQs, bboxWsenParam } from './mapBbox';
import type { PlanPoint } from '../stores/planStore';

// ---- Tile math ------------------------------------------------------

function lonToTileX(lon: number, z: number): number {
  return Math.floor(((lon + 180) / 360) * (1 << z));
}
function latToTileY(lat: number, z: number): number {
  const rad = (lat * Math.PI) / 180;
  return Math.floor(
    ((1 - Math.log(Math.tan(rad) + 1 / Math.cos(rad)) / Math.PI) / 2) * (1 << z),
  );
}

// 1 nm ≈ 1852 m; 1 degree latitude ≈ 60 nm. Longitude degree shrinks
// with cos(lat). Buffer the corridor by nm in lat/lon space.
function bufferBbox(
  bbox: [number, number, number, number],
  bufferNm: number,
): [number, number, number, number] {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const dLat = bufferNm / 60;
  const meanLat = (minLat + maxLat) / 2;
  const dLon = bufferNm / (60 * Math.max(0.1, Math.cos((meanLat * Math.PI) / 180)));
  return [minLon - dLon, minLat - dLat, maxLon + dLon, maxLat + dLat];
}

function bboxFromCoords(coords: Array<[number, number]>): [number, number, number, number] {
  let minLon = Infinity, minLat = Infinity, maxLon = -Infinity, maxLat = -Infinity;
  for (const [lon, lat] of coords) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLon, minLat, maxLon, maxLat];
}

/** Build the route bbox: densified great-circle from route points + buffer. */
export function routeBbox(route: PlanPoint[], bufferNm: number): {
  bbox: [number, number, number, number];
  totalNm: number;
} {
  if (route.length < 2) {
    if (route.length === 1) {
      const r = route[0];
      const buffered = bufferBbox([r.lon, r.lat, r.lon, r.lat], bufferNm);
      return { bbox: buffered, totalNm: 0 };
    }
    return { bbox: [-180, -85, 180, 85], totalNm: 0 };
  }
  const densified = densifyRouteForGreatCircle(route, 25);
  let totalNm = 0;
  for (let i = 1; i < route.length; i++) {
    totalNm += greatCircleNm(route[i - 1].lat, route[i - 1].lon, route[i].lat, route[i].lon);
  }
  const bbox = bufferBbox(bboxFromCoords(densified), bufferNm);
  return { bbox, totalNm };
}

/** Convert tile (z, x, y) coords to the lat/lon of the tile's center. */
export function tileCenterLatLon(z: number, x: number, y: number): { lat: number; lon: number } {
  const n = 1 << z;
  const lon = ((x + 0.5) / n) * 360 - 180;
  const latRad = Math.atan(Math.sinh(Math.PI * (1 - (2 * (y + 0.5)) / n)));
  return { lat: (latRad * 180) / Math.PI, lon };
}

/**
 * Great-circle distance (nm) from a tile's center to the nearest vertex
 * on a densified route line. Used by buildPackUrls() to corridor-shape
 * high-zoom tile selection: tiles farther than ~20 nm from the route
 * are skipped, since the pilot won't pan/zoom there during flight.
 *
 * `densifiedRoute` must be tightly densified (~5 nm step) so the
 * nearest-vertex approximation is close to the true nearest-point-on-line.
 */
export function tileDistanceToRouteNm(
  z: number,
  x: number,
  y: number,
  densifiedRoute: Array<[number, number]>,
): number {
  if (densifiedRoute.length === 0) return Infinity;
  const c = tileCenterLatLon(z, x, y);
  let minNm = Infinity;
  for (const [rlon, rlat] of densifiedRoute) {
    const d = greatCircleNm(c.lat, c.lon, rlat, rlon);
    if (d < minNm) minNm = d;
  }
  return minNm;
}

/** Enumerate tile coords (z, x, y) covering a bbox at a given zoom. */
export function tilesInBbox(
  bbox: [number, number, number, number],
  z: number,
): Array<{ z: number; x: number; y: number }> {
  const [minLon, minLat, maxLon, maxLat] = bbox;
  const xMin = lonToTileX(minLon, z);
  const xMax = lonToTileX(maxLon, z);
  // Note: latToTileY inverts (north → lower y).
  const yMin = latToTileY(maxLat, z);
  const yMax = latToTileY(minLat, z);
  const out: Array<{ z: number; x: number; y: number }> = [];
  for (let x = Math.max(0, xMin); x <= Math.min((1 << z) - 1, xMax); x++) {
    for (let y = Math.max(0, yMin); y <= Math.min((1 << z) - 1, yMax); y++) {
      out.push({ z, x, y });
    }
  }
  return out;
}

// ---- URL builders ---------------------------------------------------

const DEFAULT_BASE_LAYER = 'satellite'; // base imagery shipped through /api/tile
const DEFAULT_CHART_LAYER = 'sectional'; // chart overlay; could be ifr-low etc.
const TERRAIN_URL = (z: number, x: number, y: number) => `/api/wx/terrain?z=${z}&x=${x}&y=${y}`;
const TILE_URL = (layer: string, z: number, x: number, y: number) =>
  `/api/tile?layer=${layer}&z=${z}&x=${x}&y=${y}`;

/** Per-layer native maximum zoom level. Upstream (vfrmap.com etc.)
 *  doesn't publish tiles above these zooms; pack enumeration MUST
 *  clamp to these so we don't generate hundreds of guaranteed-404
 *  URLs ("hundreds of charts failing to cache", boss 2026-05-25). */
export const CHART_LAYER_MAX_ZOOM: Record<string, number> = {
  sectional: 12,
  helo: 13,
  'ifr-low': 11,
  'ifr-high': 10,
  topo: 16,
  satellite: 19,
};

/** Choose the set of chart layers appropriate for the planned flight.
 *  Logic (boss spec 2026-05-25 16:15 PT):
 *    - High IFR (FL180+)   → ifr-high only
 *    - Low IFR             → sectional + ifr-low
 *    - Any VFR             → sectional
 *    - Unknown rule        → sectional (safe default)
 *  Always prepends `prefer` (the chart currently active on the user's
 *  map) so the visible chart is guaranteed packed even if our rule
 *  derivation disagrees with the pilot. */
export function chooseChartLayers(opts: {
  prefer?: string | null;
  cruiseAltFt?: number | null;
  rule?: 'vfr' | 'ifr' | null;
}): string[] {
  const out: string[] = [];
  const add = (l: string) => {
    if (l && l !== 'none' && !out.includes(l)) out.push(l);
  };
  if (opts.prefer) add(opts.prefer);
  const isHigh = (opts.cruiseAltFt ?? 0) >= 18_000;
  if (opts.rule === 'ifr') {
    if (isHigh) add('ifr-high');
    else { add('sectional'); add('ifr-low'); }
  } else if (opts.rule === 'vfr') {
    add('sectional');
  } else {
    // No rule — follow altitude alone as a hint, default sectional.
    add('sectional');
    if (isHigh) add('ifr-high');
  }
  return out;
}

/** Build the full list of URLs that constitute "the pack" for a route. */
/** Categories used by the Pack progress UI. Order here is render
 *  order in the progress sheet. */
export const PACK_CATEGORIES = [
  'dem',           // Terrain/DEM tiles (TAWS overlay)
  'basemap',       // Base imagery tiles
  'chart',         // Sectional / IFR / helicopter chart overlay tiles
  'airspace',      // SUA / TFR / airways bbox
  'airports',      // Per-airport detail + NASR + diagrams + procedures + aircraft profile
  'navaids',       // VOR/NDB/DME inside the corridor bbox
  'fixes',         // IFR fixes inside the corridor bbox
  'plates',        // Approach plate PDFs (route + alternate + diversion airports)
  // —— retained for back-compat with manifests from before weather
  //    overlays were dropped (boss 2026-05-25); no fresh URLs land here
  //    anymore, so totals will be 0 and they'll be hidden in the sheet.
  'metar-taf',
  'winds',
  'wx-area',
  'fires',         // Active fires + VIIRS hotspots
  'other',         // Anything we couldn't classify
] as const;

export type PackCategory = typeof PACK_CATEGORIES[number];

/** Human-readable label for each category (shown in the progress sheet). */
export const PACK_CATEGORY_LABEL: Record<PackCategory, string> = {
  'dem':         'Terrain (DEM)',
  'basemap':     'Base map',
  'chart':       'Charts',
  'airspace':    'Airspace / TFRs',
  'airports':    'Airport details',
  'navaids':     'Navaids',
  'fixes':       'IFR fixes',
  'plates':      'Approach plates',
  'metar-taf':   'METAR / TAF',
  'winds':       'Winds aloft',
  'wx-area':     'Area weather',
  'fires':       'Fires / hotspots',
  'other':       'Other',
};

export interface PackUrls {
  tiles: string[];
  api: string[];
  airportIdents: string[];
  /** Per-URL category for the progress sheet. Keyed by the URL itself. */
  categoryOf: Map<string, PackCategory>;
}

/** Classify a single URL into a PackCategory. Pure function so we can
 *  test the rules and reuse on retry. */
export function classifyPackUrl(url: string): PackCategory {
  if (url.startsWith('/api/tile?')) {
    if (url.includes('layer=terrain') || url.includes('TERRAIN')) return 'dem';
    // Chart layers: sectional, IFR low/high (URL form ifr-low / ifr-high),
    // helicopter, WAC, TAC. Underscores + hyphens both tolerated.
    if (/layer=(sectional|ifr[-_]?low|ifr[-_]?high|helo?|wac|tac)\b/i.test(url)) return 'chart';
    return 'basemap';
  }
  if (url.startsWith('/api/wx/terrain')) return 'dem';
  if (url.startsWith('/api/airport-nasr')) return 'airports';
  if (url.startsWith('/api/airport?'))    return 'airports';
  if (url.startsWith('/api/wx/metar'))    return 'metar-taf';
  if (url.startsWith('/api/wx/taf'))      return 'metar-taf';
  if (url.startsWith('/api/wx/winds'))    return 'winds';
  if (url.startsWith('/api/wx/gfa'))      return 'wx-area';
  if (url.startsWith('/api/wx/cwa'))      return 'wx-area';
  if (url.startsWith('/api/sigmet'))      return 'wx-area';
  if (url.startsWith('/api/airmet'))      return 'wx-area';
  if (url.startsWith('/api/sua'))         return 'airspace';
  if (url.startsWith('/api/tfr'))         return 'airspace';
  if (url.startsWith('/api/bbox')) {
    // Bbox returns whichever feature class the `layer=` param asks for.
    if (url.includes('layer=navaids'))    return 'navaids';
    if (url.includes('layer=fixes'))      return 'fixes';
    // airports + heliports both land in the airports bucket since
    // they share the same downstream icon + airport-detail fetch.
    return 'airports';
  }
  if (url.startsWith('/api/fires'))           return 'fires';
  if (url.startsWith('/api/viirs'))           return 'fires';
  if (url.startsWith('/api/plates'))          return 'plates';
  if (url.startsWith('/api/procedures'))      return 'airports';
  if (url.startsWith('/api/airport-diagram')) return 'airports';
  if (url.startsWith('/api/airspace-class'))  return 'airspace';
  if (url.startsWith('/api/airways'))         return 'airspace';
  if (url.startsWith('/api/airway?'))         return 'airspace';
  if (url.startsWith('/api/obstacles'))       return 'airspace';
  if (url.startsWith('/api/dispatch-zones'))  return 'airspace';
  if (url.startsWith('/api/landowner'))       return 'other';
  if (url.startsWith('/api/sun'))             return 'other';
  return 'other';
}

export function buildPackUrls(opts: {
  route: PlanPoint[];
  bbox: [number, number, number, number];
  zoomMin: number;
  zoomMax: number;
  baseLayer?: string;
  /** Multiple chart layers — pack will enumerate tiles for each one
   *  it knows about, capped to that layer's native max zoom. Use
   *  `chooseChartLayers()` to derive the right set from altitude +
   *  flight rule. Falls back to a single sectional layer when not
   *  provided. */
  chartLayers?: string[] | null;
  /** Diversion airports + alternate + route airports for which to
   *  pack approach plates / procedures / NASR / detail. Order does
   *  not matter; duplicates are deduped. When omitted, only the
   *  route's own airport rows get packed. */
  airportIdents?: string[];
  /** Procedure idents (CIFP) keyed by airport, for CIFP per-airport
   *  procedure JSON. */
  procedureAirports?: string[];
  /** Full plate PDF URLs (one per d-TPP PDF) to pre-cache. Caller
   *  enumerates these by fetching /api/plates/airport?ident=X first
   *  and building /api/plates/pdf?cycle=YYCC&pdf=PDFNAME URLs. */
  platePdfUrls?: string[];
  /** Aircraft performance profile id to pre-cache. Optional. */
  aircraftProfileId?: string | null;
  /** Corridor half-width (nm) for high-zoom tile filtering. Tiles
   *  beyond this distance from the densified route line are dropped
   *  at z > overviewZoomMax. Default 20. */
  corridorDetailNm?: number;
  /** Highest zoom level treated as "overview" — bbox-shaped (every
   *  tile in the bbox kept). Levels above this are corridor-filtered.
   *  Default 8. */
  overviewZoomMax?: number;
}): PackUrls {
  const tiles: string[] = [];
  const api: string[] = [];

  const baseLayer = opts.baseLayer ?? DEFAULT_BASE_LAYER;
  const chartLayers = (opts.chartLayers && opts.chartLayers.length > 0)
    ? opts.chartLayers
    : [DEFAULT_CHART_LAYER];
  const corridorDetailNm = opts.corridorDetailNm ?? 20;
  const overviewZoomMax = opts.overviewZoomMax ?? 8;

  // Tiles: DEM (for TAWS) + base imagery + (optional) chart overlay.
  //
  // At overview zooms (z <= overviewZoomMax, default 8) the bbox is
  // small enough in tile-space that we just keep every tile — useful
  // when the pilot zooms way out for situational awareness. At cruise +
  // airport-detail zooms (z > overviewZoomMax) we corridor-filter to
  // tiles within corridorDetailNm (default 20) of the densified route.
  // A 250 NM bbox at z=12 is ~5000 tiles; corridor-shaped it's ~600.
  //
  // The TILE_CAP is now a safety net — for normal flights the corridor
  // filter keeps us comfortably under it. A 1000 NM CONUS-wide flight
  // still fits.
  const TILE_CAP = 5000;
  // Densify once for the corridor distance checks. 5 nm step keeps the
  // nearest-vertex approximation close to true nearest-point-on-line.
  const densifiedLonLat = densifyRouteForGreatCircle(opts.route, 5);
  // Per-layer max zoom — don't enumerate tiles above the layer's
  // native ceiling. Falls back to opts.zoomMax for layers we don't
  // explicitly know about (e.g. future chart additions).
  const baseMaxZ = CHART_LAYER_MAX_ZOOM[baseLayer] ?? opts.zoomMax;
  const terrainMaxZ = CHART_LAYER_MAX_ZOOM.topo ?? opts.zoomMax;
  const chartLayerMaxZ: Record<string, number> = Object.fromEntries(
    chartLayers.map((l) => [l, CHART_LAYER_MAX_ZOOM[l] ?? opts.zoomMax]),
  );
  outer: for (let z = opts.zoomMin; z <= opts.zoomMax; z++) {
    const t = tilesInBbox(opts.bbox, z);
    const corridorActive = z > overviewZoomMax && densifiedLonLat.length > 0;
    for (const { x, y } of t) {
      if (corridorActive) {
        const d = tileDistanceToRouteNm(z, x, y, densifiedLonLat);
        if (d > corridorDetailNm) continue;
      }
      if (z <= terrainMaxZ) tiles.push(TERRAIN_URL(z, x, y));
      if (z <= baseMaxZ) tiles.push(TILE_URL(baseLayer, z, x, y));
      for (const layer of chartLayers) {
        if (layer && layer !== 'none' && z <= (chartLayerMaxZ[layer] ?? opts.zoomMax)) {
          tiles.push(TILE_URL(layer, z, x, y));
        }
      }
      if (tiles.length >= TILE_CAP) break outer;
    }
  }

  // Bbox-scoped API endpoints (small, fast). Pack covers everything a
  // pilot might need to navigate offline within the route corridor.
  // Weather endpoints intentionally NOT included — weather is volatile,
  // pre-caching it gives a false sense of currency (boss 2026-05-25
  // 16:15 PT: "None of the weather overlays need to be packed").
  //
  // ⚠️ URL precision must match runtime callers exactly so the SW
  // cache key from the pack fetch is hit at runtime.
  //   /api/bbox: 2 decimal places (matching fetchBbox() in mapBbox.ts)
  //   fires/viirs/tfr/sua/etc. bbox= param: 4 decimal places (matching
  //     bboxWsEn() in overlaySpecs.ts)
  // Both helpers live in mapBbox.ts — route ALL callers through them.
  const [minLon, minLat, maxLon, maxLat] = opts.bbox;
  for (const layer of ['airports', 'navaids', 'fixes', 'heliports'] as const) {
    api.push(bboxLayerQs(layer, minLon, minLat, maxLon, maxLat, opts.zoomMax));
  }
  const bboxWsen = bboxWsenParam(minLon, minLat, maxLon, maxLat);
  api.push(`/api/fires?bbox=${encodeURIComponent(bboxWsen)}`);
  api.push(`/api/viirs?bbox=${encodeURIComponent(bboxWsen)}`);
  api.push(`/api/tfr?bbox=${encodeURIComponent(bboxWsen)}`);
  api.push(`/api/sua?bbox=${encodeURIComponent(bboxWsen)}`);
  api.push(`/api/airspace-class?bbox=${encodeURIComponent(bboxWsen)}`);
  api.push(`/api/airways?bbox=${encodeURIComponent(bboxWsen)}`);
  api.push(`/api/obstacles?bbox=${encodeURIComponent(bboxWsen)}&minAglFt=200`);
  api.push(`/api/dispatch-zones?bbox=${encodeURIComponent(bboxWsen)}`);
  // Sun position for corridor airports (use first airport as reference)
  if (opts.route.length > 0) {
    const first = opts.route[0];
    const nowIso = new Date().toISOString().slice(0, 10);
    api.push(`/api/sun?lat=${first.lat.toFixed(4)}&lon=${first.lon.toFixed(4)}&date=${nowIso}`);
  }

  // Build the union of airports the pilot may need offline:
  //   - every airport row on the route
  //   - the alternate airport (passed via opts.airportIdents)
  //   - diversion airports within ~50nm of the route corridor (passed
  //     via opts.airportIdents — caller pre-fetches the /api/bbox
  //     airport listing and filters by distance to the densified route).
  const routeAirports = opts.route
    .filter((p) => (p.kind ?? 'airport') === 'airport' && p.ident)
    .map((p) => p.ident.toUpperCase());
  const allAirports = Array.from(new Set([
    ...routeAirports,
    ...(opts.airportIdents ?? []).map((s) => s.toUpperCase()),
  ]));
  for (const ident of allAirports) {
    api.push(`/api/airport?ident=${encodeURIComponent(ident)}`);
    api.push(`/api/airport-nasr?ident=${encodeURIComponent(ident)}`);
    api.push(`/api/airport-diagram?ident=${encodeURIComponent(ident)}`);
    // Approach plate listing + CIFP procedures (Tier 1 spec, boss
    // 2026-05-25 16:15 PT). The /api/plates/airport endpoint returns
    // every plate index entry; the actual PDFs are URL-enumerated by
    // the caller via fetchPlatePdfUrls() and passed in opts.platePdfUrls.
    api.push(`/api/plates/airport?ident=${encodeURIComponent(ident)}`);
    api.push(`/api/procedures?airport=${encodeURIComponent(ident)}`);
  }

  // Plate PDFs themselves (Tier 1 spec). Caller pre-resolved the list
  // by hitting /api/plates/airport?ident=X for each airport in the
  // union above, then built the per-PDF URLs.
  for (const pdfUrl of opts.platePdfUrls ?? []) {
    api.push(pdfUrl);
  }

  // Aircraft performance profile: aircraft + their profiles live in
  // aircraftStore (Supabase-synced + persisted locally on every change).
  // There is no dedicated /api/aircraft-profile endpoint to pre-fetch —
  // the store IS the cache. We thread the id through to the manifest
  // so the Pack sheet can show what aircraft this pack was tied to,
  // but no extra URL needs to land in the API list.
  void opts.aircraftProfileId;

  // Airway expansion data: pre-cache /api/airway?ident=X for any airways
  // referenced in the route's viaAirway tags, so re-applying a route
  // plan works fully offline after pack-for-flight.
  const airwayIdents = new Set<string>();
  for (const p of opts.route) {
    if ((p as { viaAirway?: string }).viaAirway) {
      airwayIdents.add((p as { viaAirway?: string }).viaAirway!);
    }
  }
  for (const awy of airwayIdents) {
    api.push(`/api/airway?ident=${encodeURIComponent(awy)}`);
  }

  // Build the category index. Tile classification is data-driven from
  // the URL itself; the API classifier handles the bbox + airport-detail
  // endpoints above.
  const categoryOf = new Map<string, PackCategory>();
  for (const t of tiles) categoryOf.set(t, classifyPackUrl(t));
  for (const u of api)   categoryOf.set(u, classifyPackUrl(u));

  return { tiles, api, airportIdents: allAirports, categoryOf };
}

// ---- Fetcher --------------------------------------------------------

async function fetchSwallow(url: string): Promise<{ ok: boolean; bytes: number }> {
  try {
    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) {
      // HTTP errors are expected for out-of-coverage tiles; only warn
      // on server errors (5xx) to avoid log spam on 404 tiles.
      if (res.status >= 500) {
        console.warn('[pack] upstream %d: %s', res.status, url);
      }
      return { ok: false, bytes: 0 };
    }
    // Drain the body so the SW captures the full response. We don't
    // need the content here \u2014 the service worker has already cached it.
    const blob = await res.blob();
    return { ok: true, bytes: blob.size };
  } catch (e) {
    console.warn('[pack] network error fetching:', url, e);
    return { ok: false, bytes: 0 };
  }
}

/** Run an async task list with bounded concurrency. */
async function runWithConcurrency<T>(
  items: T[],
  worker: (item: T, index: number) => Promise<void>,
  concurrency: number,
  abortSignal?: AbortSignal,
): Promise<void> {
  let next = 0;
  const runners = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (true) {
      if (abortSignal?.aborted) return;
      const idx = next++;
      if (idx >= items.length) return;
      try { await worker(items[idx], idx); } catch (e) {
        console.warn('[pack] worker error at index', idx, ':', e);
      }
    }
  });
  await Promise.all(runners);
}

// ---- Pack store -----------------------------------------------------

export type PackPhase = 'idle' | 'planning' | 'downloading' | 'ready' | 'failed' | 'canceled';

export interface PackCategoryProgress {
  category: PackCategory;
  /** Total URLs in this category. */
  total: number;
  /** Successfully cached. */
  ok: number;
  /** Failed (HTTP error or network failure). */
  fail: number;
  /** URLs that failed; available for retry. */
  failedUrls: string[];
}

export interface PackManifest {
  id: string;
  label: string;
  /** Optional id of the flight that owns this pack — used by
   *  flight-scoped UIs so a completed pack from a different flight
   *  doesn't bleed into the Pack panel of the flight you're viewing
   *  now. Null = unscoped (e.g. PlanPanel "current flight plan" pack). */
  scopeFlightId?: string | null;
  createdAt: number;
  routeIdents: string[];
  bbox: [number, number, number, number];
  bufferNm: number;
  zoomMin: number;
  zoomMax: number;
  baseLayer: string;
  /** Chart layers packed (multi-layer per altitude/rule rule). Old
   *  manifests written before 2026-05-25 only have `chartLayer` (single);
   *  keep both fields so historic packs still parse. */
  chartLayer: string | null;
  chartLayers?: string[];
  total: number;
  bytes: number;
  ok: number;
  fail: number;
  /** Pre-download size estimate in MB (50 KB/tile + 20 KB/api). Shown
   *  in the PackProgressSheet header so the pilot sees the budget
   *  BEFORE the first byte arrives. Optional for back-compat with
   *  older manifests in localStorage. */
  estMb?: number;
  /** Tile-URL count at manifest time (3 URLs per unique tile when DEM
   *  + base + chart are all enabled). Useful in the sheet header
   *  alongside estMb. */
  tileCount?: number;
  /** Per-category breakdown. Present after every pack run; absent for
   *  older manifests loaded from localStorage prior to this field. */
  categories?: PackCategoryProgress[];
}

interface PackState {
  phase: PackPhase;
  current: PackManifest | null;
  saved: PackManifest[];
  /** Last status text (e.g. 'Caching DEM tiles \u2026'). */
  status: string;
  /** Cancellation handle for the in-flight pack. */
  _abort: AbortController | null;

  hydrate: () => void;
  startPack: (opts: {
    route: PlanPoint[];
    label?: string;
    /** Flight id this pack belongs to. Scopes the PackProgressSheet so
     *  it only renders for the same flight on subsequent renders. */
    scopeFlightId?: string | null;
    bufferNm?: number;
    zoomMin?: number;
    zoomMax?: number;
    baseLayer?: string;
    /** Single layer (legacy) — if provided, used as the "prefer" hint
     *  for chooseChartLayers. Use chartLayers below for explicit
     *  multi-layer control. */
    chartLayer?: string | null;
    /** Explicit list of chart layers to pack. Overrides the
     *  altitude+rule derivation when provided. */
    chartLayers?: string[] | null;
    /** Cruise altitude (ft MSL) of the planned flight. Used with
     *  cruiseRule to pick chart layers if chartLayers is not
     *  explicitly provided. */
    cruiseAlt?: number | null;
    /** Flight rule of the planned flight. Used with cruiseAlt to
     *  pick chart layers. */
    cruiseRule?: 'vfr' | 'ifr' | null;
    /** Alternate airport ICAO/FAA ident. Plates + procedures + NASR
     *  are packed for this on top of the route airports. */
    alternateIdent?: string | null;
    /** Aircraft performance-profile id. Performance JSON is packed
     *  for this aircraft so the in-flight calc panel works offline. */
    aircraftProfileId?: string | null;
    /** Half-width (nm) of the diversion-airport corridor: airports
     *  inside the route bbox AND within this distance of the
     *  densified route line get plates+procedures+NASR packed.
     *  Default 50 (boss spec 2026-05-25 16:15 PT). */
    diversionCorridorNm?: number;
    /** Cap on how many diversion airports to enumerate (per pack).
     *  Each adds ~6 API calls + N plate PDFs. Default 25. */
    maxDiversionAirports?: number;
    /** Corridor half-width (nm) for high-zoom tile filtering. Default 20. */
    corridorDetailNm?: number;
    /** Highest zoom kept bbox-shaped; above this, tiles are corridor-filtered. Default 8. */
    overviewZoomMax?: number;
  }) => Promise<void>;
  /** Retry only the failed URLs in the given category. */
  retryCategory: (category: PackCategory) => Promise<void>;
  /** Retry every failed URL across all categories. */
  retryAllFailed: () => Promise<void>;
  cancel: () => void;
  reset: () => void;
  clearSaved: () => Promise<void>;
}

const SAVED_LS_KEY = 'fyrefly.packs.v1';

// ---- SW cache-prune helper ------------------------------------------
//
// HI-6' (2026-05-24): post-write byte budget enforcement in the SW
// fires automatically, but it only activates after individual writes
// cross the threshold. Posting an explicit cache-prune message at pack
// lifecycle boundaries (start, completion, cancel) gives the SW a
// guaranteed enforcement point so we don't over-fill during a large
// pack run and risk an iOS origin eviction.

function postSwCachePrune(): void {
  if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
    navigator.serviceWorker.controller.postMessage({ type: 'cache-prune' });
  }
}

function readSaved(): PackManifest[] {
  if (typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(SAVED_LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeSaved(saved: PackManifest[]): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SAVED_LS_KEY, JSON.stringify(saved));
  } catch {
    // localStorage full or unavailable; silently skip.
  }
}

// ---- Pre-flight enumeration helpers --------------------------------

/** Densified route as [lon, lat] pairs at ~5 nm step, for nearest-point
 *  proximity checks against airports / diversion candidates. */
function densifyForCorridor(route: PlanPoint[]): Array<[number, number]> {
  if (route.length < 2) return route.map((p) => [p.lon, p.lat] as [number, number]);
  return densifyRouteForGreatCircle(route, 5);
}

/** Great-circle distance from a point to the nearest vertex on the
 *  densified route line (nm). */
function pointDistanceToRouteNm(
  lat: number,
  lon: number,
  densified: Array<[number, number]>,
): number {
  let best = Infinity;
  for (const [rlon, rlat] of densified) {
    const d = greatCircleNm(lat, lon, rlat, rlon);
    if (d < best) best = d;
  }
  return best;
}

/** Fetch the bbox airports listing and filter to those within
 *  `corridorNm` of the route. Returns sorted-by-distance idents. */
async function fetchDiversionAirports(opts: {
  bbox: [number, number, number, number];
  zoomMax: number;
  route: PlanPoint[];
  corridorNm: number;
  signal?: AbortSignal;
  cap: number;
}): Promise<string[]> {
  const [minLon, minLat, maxLon, maxLat] = opts.bbox;
  const url = bboxLayerQs('airports', minLon, minLat, maxLon, maxLat, opts.zoomMax);
  try {
    const r = await fetch(url, { signal: opts.signal });
    if (!r.ok) return [];
    const body = (await r.json()) as {
      features?: Array<{
        properties?: { ident?: string };
        geometry?: { coordinates?: [number, number] };
      }>;
    };
    const densified = densifyForCorridor(opts.route);
    const routeIdents = new Set(
      opts.route.map((p) => (p.ident || '').toUpperCase()).filter(Boolean),
    );
    const candidates: Array<{ ident: string; distNm: number }> = [];
    for (const f of body.features ?? []) {
      const ident = (f.properties?.ident ?? '').toUpperCase();
      const coords = f.geometry?.coordinates;
      if (!ident || !coords || coords.length < 2) continue;
      if (routeIdents.has(ident)) continue; // already on route
      const [lon, lat] = coords;
      const d = pointDistanceToRouteNm(lat, lon, densified);
      if (d <= opts.corridorNm) {
        candidates.push({ ident, distNm: d });
      }
    }
    candidates.sort((a, b) => a.distNm - b.distNm);
    return candidates.slice(0, opts.cap).map((c) => c.ident);
  } catch {
    return [];
  }
}

/** For each ident, fetch /api/plates/airport and collect /api/plates/pdf
 *  URLs for every plate in the index. Failures are silently skipped
 *  (airport without published plates, foreign airport, etc.). */
async function fetchPlatePdfUrls(
  idents: string[],
  signal?: AbortSignal,
): Promise<string[]> {
  const out: string[] = [];
  // Bounded concurrency — 6 simultaneous fetches keep planning quick
  // without saturating the connection pool when 20+ airports are
  // queued.
  let next = 0;
  const seen = new Set<string>();
  await Promise.all(Array.from({ length: 6 }, async () => {
    while (true) {
      if (signal?.aborted) return;
      const i = next++;
      if (i >= idents.length) return;
      const ident = idents[i];
      try {
        const r = await fetch(
          `/api/plates/airport?ident=${encodeURIComponent(ident)}`,
          { signal },
        );
        if (!r.ok) continue;
        const body = (await r.json()) as {
          cycle?: string;
          plates?: Array<{ pdfName?: string }>;
        };
        const cycle = body.cycle;
        if (!cycle) continue;
        for (const p of body.plates ?? []) {
          if (!p.pdfName) continue;
          const url = `/api/plates/pdf?cycle=${encodeURIComponent(cycle)}&pdf=${encodeURIComponent(p.pdfName)}`;
          if (!seen.has(url)) {
            seen.add(url);
            out.push(url);
          }
        }
      } catch { /* ignore one-airport failures */ }
    }
  }));
  return out;
}

export const usePackStore = create<PackState>((set, get) => ({
  phase: 'idle',
  current: null,
  saved: [],
  status: '',
  _abort: null,

  hydrate: () => {
    set({ saved: readSaved() });
  },

  startPack: async (opts) => {
    const route = opts.route;
    if (route.length < 1) return;
    const abort = new AbortController();
    set({ phase: 'planning', status: 'Planning route corridor\u2026', _abort: abort });
    // Prune before we start so the SW is under budget before a
    // potentially large tile+API batch lands (HI-6' defense in depth).
    postSwCachePrune();

    const bufferNm = opts.bufferNm ?? 50;
    const zoomMin = opts.zoomMin ?? 6;
    const zoomMax = opts.zoomMax ?? 12;
    const diversionCorridorNm = opts.diversionCorridorNm ?? 50;
    const maxDiversionAirports = opts.maxDiversionAirports ?? 25;
    const { bbox } = routeBbox(route, bufferNm);

    // 1) Choose chart layers. Explicit list wins; otherwise derive from
    //    altitude + rule. Single legacy chartLayer becomes a "prefer"
    //    hint so the user's currently-visible chart is always packed.
    const chartLayers = opts.chartLayers && opts.chartLayers.length > 0
      ? opts.chartLayers
      : chooseChartLayers({
          prefer: opts.chartLayer ?? null,
          cruiseAltFt: opts.cruiseAlt ?? null,
          rule: opts.cruiseRule ?? null,
        });

    // 2) Discover diversion airports within 50 nm of the route corridor.
    //    Boss spec 2026-05-25 16:15 PT. Capped to keep planning quick.
    set({ status: 'Finding diversion airports...' });
    const diversionIdents = await fetchDiversionAirports({
      bbox,
      zoomMax,
      route,
      corridorNm: diversionCorridorNm,
      signal: abort.signal,
      cap: maxDiversionAirports,
    });

    // 3) Union of airports to fully pack: route + alternate + diversion.
    const routeAirportIdents = route
      .filter((p) => (p.kind ?? 'airport') === 'airport' && p.ident)
      .map((p) => p.ident.toUpperCase());
    const alt = opts.alternateIdent ? opts.alternateIdent.toUpperCase() : null;
    const allAirportIdents = Array.from(new Set([
      ...routeAirportIdents,
      ...(alt ? [alt] : []),
      ...diversionIdents,
    ]));

    // 4) Enumerate plate PDF URLs for the airport union.
    set({ status: `Enumerating plates for ${allAirportIdents.length} airports...` });
    const platePdfUrls = await fetchPlatePdfUrls(allAirportIdents, abort.signal);

    set({ status: 'Planning route corridor...' });
    const urls = buildPackUrls({
      route,
      bbox,
      zoomMin,
      zoomMax,
      baseLayer: opts.baseLayer,
      chartLayers,
      airportIdents: allAirportIdents,
      platePdfUrls,
      aircraftProfileId: opts.aircraftProfileId ?? null,
      corridorDetailNm: opts.corridorDetailNm,
      overviewZoomMax: opts.overviewZoomMax,
    });
    const all = [...urls.tiles, ...urls.api];

    const label =
      opts.label ??
      (route.length >= 2 ? `${route[0].ident} \u2192 ${route[route.length - 1].ident}` : route[0].ident);

    // Pre-compute per-category totals so the progress sheet has shape
    // before any download finishes.
    const catMap = new Map<PackCategory, PackCategoryProgress>();
    for (const cat of PACK_CATEGORIES) {
      catMap.set(cat, { category: cat, total: 0, ok: 0, fail: 0, failedUrls: [] });
    }
    for (const u of all) {
      const cat = urls.categoryOf.get(u) ?? 'other';
      catMap.get(cat)!.total += 1;
    }
    const initialCategories = Array.from(catMap.values()).filter((c) => c.total > 0);

    const manifest: PackManifest = {
      id: `pack-${Date.now().toString(36)}`,
      label,
      scopeFlightId: opts.scopeFlightId ?? null,
      createdAt: Date.now(),
      routeIdents: route.map((p) => p.ident),
      bbox,
      bufferNm,
      zoomMin,
      zoomMax,
      baseLayer: opts.baseLayer ?? DEFAULT_BASE_LAYER,
      chartLayer: chartLayers[0] ?? null,
      chartLayers,
      total: all.length,
      bytes: 0,
      ok: 0,
      fail: 0,
      estMb: estimatePackSizeMb(urls),
      tileCount: urls.tiles.length,
      categories: initialCategories,
    };
    set({ phase: 'downloading', current: manifest, status: `Caching ${all.length} assets\u2026` });

    let lastUiUpdate = 0;
    let okCount = 0;
    let failCount = 0;
    let byteCount = 0;
    await runWithConcurrency(
      all,
      async (url) => {
        const r = await fetchSwallow(url);
        const cat = urls.categoryOf.get(url) ?? 'other';
        const c = catMap.get(cat)!;
        if (r.ok) {
          okCount++;
          byteCount += r.bytes;
          c.ok += 1;
        } else {
          failCount++;
          c.fail += 1;
          c.failedUrls.push(url);
        }
        const now = Date.now();
        if (now - lastUiUpdate > 200) {
          lastUiUpdate = now;
          const snap = Array.from(catMap.values()).filter((cc) => cc.total > 0)
            .map((cc) => ({ ...cc, failedUrls: [...cc.failedUrls] }));
          set((state) => ({
            current: state.current
              ? { ...state.current, ok: okCount, fail: failCount, bytes: byteCount, categories: snap }
              : state.current,
            status: `Caching\u2026 ${okCount + failCount} / ${all.length}`,
          }));
        }
      },
      8,
      abort.signal,
    );

    if (abort.signal.aborted) {
      set({ phase: 'canceled', status: 'Pack canceled.', _abort: null });
      // Post explicit cache-prune so the SW enforces budget at cancel
      // boundary, even if the inline post-write trigger didn't fire yet.
      postSwCachePrune();
      return;
    }

    const finalCategories = Array.from(catMap.values()).filter((c) => c.total > 0)
      .map((c) => ({ ...c, failedUrls: [...c.failedUrls] }));
    const finalManifest: PackManifest = {
      ...manifest,
      total: all.length,
      ok: okCount,
      fail: failCount,
      bytes: byteCount,
      categories: finalCategories,
    };
    const saved = [finalManifest, ...readSaved().filter((m) => m.id !== finalManifest.id)].slice(0, 10);
    writeSaved(saved);
    set({
      phase: failCount === all.length ? 'failed' : 'ready',
      current: finalManifest,
      saved,
      status:
        failCount === 0
          ? `Pack ready (${okCount} assets, ${formatBytes(byteCount)}).`
          : `Pack ready: ${okCount} ok, ${failCount} failed (${formatBytes(byteCount)}).`,
      _abort: null,
    });
    // Post explicit cache-prune at pack completion so the SW enforces
    // budget now that all tiles are in cache (HI-6' defense in depth).
    postSwCachePrune();
  },

  cancel: () => {
    const a = get()._abort;
    if (a) a.abort();
  },

  retryCategory: async (category) => {
    const cur = get().current;
    if (!cur || !cur.categories) return;
    const c = cur.categories.find((cc) => cc.category === category);
    if (!c || c.failedUrls.length === 0) return;
    await retryUrls(c.failedUrls, category, set, get);
  },

  retryAllFailed: async () => {
    const cur = get().current;
    if (!cur || !cur.categories) return;
    const all: Array<{ url: string; category: PackCategory }> = [];
    for (const c of cur.categories) {
      for (const u of c.failedUrls) all.push({ url: u, category: c.category });
    }
    if (all.length === 0) return;
    await retryUrlsMixed(all, set, get);
  },

  reset: () => {
    set({ phase: 'idle', current: null, status: '' });
  },

  clearSaved: async () => {
    writeSaved([]);
    if (typeof navigator !== 'undefined' && navigator.serviceWorker?.controller) {
      // Ask the SW to drop its caches too.
      navigator.serviceWorker.controller.postMessage({ type: 'cache-clear' });
    }
    set({ saved: [], phase: 'idle', current: null, status: '' });
  },
}));

// ---- Retry helpers ----------------------------------------------------
//
// retryCategory / retryAllFailed call into these. We pull the URLs out
// of the current manifest's categories[], re-fetch them, and update
// the same manifest in place so the progress sheet shows the retry
// results without forcing a re-pack of everything.

import type { StoreApi } from 'zustand';

async function retryUrls(
  urls: string[],
  category: PackCategory,
  set: StoreApi<PackState>['setState'],
  get: StoreApi<PackState>['getState'],
): Promise<void> {
  if (urls.length === 0) return;
  // Move-in-place: pull the failed URLs out of the manifest, run them,
  // re-attribute ok/fail to the same category bucket. Other categories
  // are untouched.
  const cur = get().current;
  if (!cur || !cur.categories) return;
  // Clone the manifest so reactivity fires.
  const nextCats = cur.categories.map((c) =>
    c.category === category ? { ...c, failedUrls: [] } : c,
  );
  set({
    current: { ...cur, categories: nextCats, fail: cur.fail - urls.length },
    status: `Retrying ${urls.length} ${PACK_CATEGORY_LABEL[category]} item(s)\u2026`,
  });

  let okCount = 0;
  let failCount = 0;
  let byteCount = 0;
  const stillFailed: string[] = [];
  await runWithConcurrency(
    urls,
    async (url) => {
      const r = await fetchSwallow(url);
      if (r.ok) {
        okCount++;
        byteCount += r.bytes;
      } else {
        failCount++;
        stillFailed.push(url);
      }
    },
    8,
  );

  // Merge results back into the manifest.
  const after = get().current;
  if (!after || !after.categories) return;
  const merged = after.categories.map((c) =>
    c.category === category
      ? { ...c, ok: c.ok + okCount, fail: stillFailed.length, failedUrls: stillFailed }
      : c,
  );
  set({
    current: {
      ...after,
      ok: after.ok + okCount,
      fail: after.fail - okCount,
      bytes: after.bytes + byteCount,
      categories: merged,
    },
    status: stillFailed.length > 0
      ? `Retry: ${okCount} ok, ${failCount} still failing.`
      : `Retry: ${okCount} ok.`,
  });
}

async function retryUrlsMixed(
  items: Array<{ url: string; category: PackCategory }>,
  set: StoreApi<PackState>['setState'],
  get: StoreApi<PackState>['getState'],
): Promise<void> {
  // Bucket by category so we can update each progress entry correctly.
  const byCat = new Map<PackCategory, string[]>();
  for (const it of items) {
    if (!byCat.has(it.category)) byCat.set(it.category, []);
    byCat.get(it.category)!.push(it.url);
  }
  for (const [cat, urls] of byCat) {
    await retryUrls(urls, cat, set, get);
  }
}

/**
 * Rough size estimate (MB) for a pack, used in the PackProgressSheet
 * header so the pilot sees the budget BEFORE the download starts.
 *
 * Heuristic averages:
 *   • tiles ≈ 50 KB (PNG/WEBP, varies with terrain/imagery layer)
 *   • api ≈ 20 KB (JSON; airport-detail is heaviest, bbox feature
 *     responses are lighter)
 *
 * Within ~±30% of actual for typical CA / Pacific Northwest routes —
 * good enough for "Est: ~62 MB" UI. Don't use this for billing.
 */
export function estimatePackSizeMb(urls: PackUrls): number {
  const tilesMb = urls.tiles.length * 0.05;
  const apiMb = urls.api.length * 0.02;
  return Math.round(tilesMb + apiMb);
}

export function formatBytes(b: number): string {
  if (!Number.isFinite(b) || b <= 0) return '0 B';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
